import React, { useState, useEffect } from 'react';
import { Database, Save, RefreshCw, CheckCircle, XCircle, Eye, EyeOff, AlertCircle, Download, Upload, FileText, Shield } from 'lucide-react';
import { getSupabaseClient, updateSupabaseCredentials } from '../lib/supabase';

interface ApiSettingsProps {}

const ApiSettings: React.FC<ApiSettingsProps> = () => {
  const [apiUrl, setApiUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [currentConnection, setCurrentConnection] = useState<{ url: string; project: string } | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<string>('');
  const [supabase, setSupabase] = useState<any>(null);
  const [adminPin, setAdminPin] = useState('');
  const [showAdminPin, setShowAdminPin] = useState(false);
  const [isSavingPin, setIsSavingPin] = useState(false);
  const [pinMessage, setPinMessage] = useState('');
  const [pinStatus, setPinStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const client = getSupabaseClient();
    setSupabase(client);
    loadCurrentSettings();
    testCurrentConnection();
  }, []);

  const loadCurrentSettings = async () => {
    try {
      let url = import.meta.env.VITE_SUPABASE_URL || '';
      let key = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

      const storedUrl = localStorage.getItem('supabase_url');
      const storedKey = localStorage.getItem('supabase_anon_key');

      if (storedUrl) url = storedUrl;
      if (storedKey) key = storedKey;

      const currentClient = getSupabaseClient();
      if (currentClient && !storedUrl && !storedKey) {
        const { data } = await currentClient
          .from('admin_settings')
          .select('setting_value')
          .eq('setting_key', 'supabase_credentials')
          .maybeSingle();

        if (data?.setting_value) {
          url = data.setting_value.url || url;
          key = data.setting_value.key || key;
        }
      }

      setApiUrl(url);
      setApiKey(key);

      if (url) {
        const projectId = url.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1];
        if (projectId) {
          setCurrentConnection({
            url: url,
            project: projectId
          });
        }
      }
    } catch (error) {
      console.error('Settings load error:', error);
    }
  };

  const testCurrentConnection = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setConnectionStatus('error');
      setStatusMessage('Supabase yapılandırılmamış');
      return;
    }

    try {
      const { error } = await supabase.from('admin_settings').select('*').limit(1);

      if (error) {
        setConnectionStatus('error');
        setStatusMessage('Bağlantı hatası: ' + error.message);
      } else {
        setConnectionStatus('success');
        setStatusMessage('Bağlantı başarılı');
      }
    } catch (error: any) {
      setConnectionStatus('error');
      setStatusMessage('Bağlantı testi başarısız: ' + error.message);
    }
  };

  const testConnection = async () => {
    if (!apiUrl || !apiKey || apiUrl.trim() === '' || apiKey.trim() === '') {
      setStatusMessage('Lütfen tüm alanları doldurun');
      setConnectionStatus('error');
      return;
    }

    setIsTesting(true);
    setConnectionStatus('idle');
    setStatusMessage('Bağlantı test ediliyor...');

    try {
      const { createClient } = await import('@supabase/supabase-js');
      const testClient = createClient(apiUrl, apiKey);

      const { data, error } = await testClient.from('admin_settings').select('*').limit(1);

      if (error) {
        setConnectionStatus('error');
        setStatusMessage('Bağlantı başarısız: ' + error.message);
      } else {
        setConnectionStatus('success');
        setStatusMessage('Bağlantı başarılı! Kayıt edebilirsiniz.');

        const projectId = apiUrl.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1];
        if (projectId) {
          setCurrentConnection({
            url: apiUrl,
            project: projectId
          });
        }
      }
    } catch (error: any) {
      setConnectionStatus('error');
      setStatusMessage('Bağlantı hatası: ' + error.message);
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (!apiUrl || !apiKey) {
      setStatusMessage('Lütfen tüm alanları doldurun');
      setConnectionStatus('error');
      return;
    }

    if (connectionStatus !== 'success') {
      setStatusMessage('Lütfen önce bağlantıyı test edin');
      setConnectionStatus('error');
      return;
    }

    setIsSaving(true);

    try {
      updateSupabaseCredentials(apiUrl, apiKey);

      const supabase = getSupabaseClient();
      if (supabase) {
        const { error } = await supabase
          .from('admin_settings')
          .upsert({
            setting_key: 'supabase_credentials',
            setting_value: {
              url: apiUrl,
              key: apiKey,
              updated_at: new Date().toISOString()
            },
            description: 'Supabase API bağlantı bilgileri (URL ve Anon Key)'
          }, {
            onConflict: 'setting_key'
          })
          .select()
          .maybeSingle();

        if (error) {
          throw new Error('Veritabanına kayıt hatası: ' + error.message);
        }

        setStatusMessage('API ayarları veritabanına ve localStorage\'a kaydedildi! Sayfa yenileniyor...');
      } else {
        setStatusMessage('API ayarları localStorage\'a kaydedildi! Sayfa yenileniyor...');
      }

      setConnectionStatus('success');

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      setStatusMessage('Kayıt hatası: ' + error.message);
      setConnectionStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const generateSqlInsert = (tableName: string, data: any[]): string => {
    if (!data || data.length === 0) return '';

    let sql = `-- ${tableName} tablosu için veriler\n`;
    sql += `DELETE FROM ${tableName};\n\n`;

    for (const row of data) {
      const columns = Object.keys(row);
      const values = columns.map(col => {
        const value = row[col];

        if (value === null || value === undefined) {
          return 'NULL';
        }

        if (typeof value === 'boolean') {
          return value ? 'TRUE' : 'FALSE';
        }

        if (typeof value === 'number') {
          return value.toString();
        }

        if (typeof value === 'object') {
          return `'${JSON.stringify(value).replace(/'/g, "''")}'::jsonb`;
        }

        return `'${value.toString().replace(/'/g, "''")}'`;
      });

      sql += `INSERT INTO ${tableName} (${columns.join(', ')}) VALUES (${values.join(', ')});\n`;
    }

    sql += '\n';
    return sql;
  };

  const exportDatabase = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setImportStatus('Veritabanı bağlantısı yok');
      return;
    }

    setIsExporting(true);
    setImportStatus('Veriler dışa aktarılıyor...');

    try {
      const tables = ['quick_replies', 'response_patterns', 'welcome_messages', 'admin_settings'];
      let sqlContent = `-- Gürsu AI Veritabanı Yedeği\n`;
      sqlContent += `-- Oluşturulma Tarihi: ${new Date().toISOString()}\n`;
      sqlContent += `-- Otomatik olarak oluşturulmuştur\n\n`;
      sqlContent += `BEGIN;\n\n`;

      for (const table of tables) {
        const { data, error } = await supabase
          .from(table)
          .select('*');

        if (error) throw new Error(`${table} tablosu dışa aktarılamadı: ${error.message}`);

        if (data && data.length > 0) {
          sqlContent += generateSqlInsert(table, data);
        }
      }

      sqlContent += `COMMIT;\n`;

      const blob = new Blob([sqlContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `database-backup-${new Date().toISOString().split('T')[0]}.sql`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setImportStatus('Veritabanı başarıyla SQL formatında dışa aktarıldı!');
      setTimeout(() => setImportStatus(''), 3000);
    } catch (error: any) {
      setImportStatus('Dışa aktarma hatası: ' + error.message);
      setTimeout(() => setImportStatus(''), 5000);
    } finally {
      setIsExporting(false);
    }
  };

  const parseSqlValue = (value: string, isJsonb: boolean = false): any => {
    if (value === 'NULL') return null;
    if (value === 'TRUE') return true;
    if (value === 'FALSE') return false;

    if (isJsonb) {
      const jsonMatch = value.match(/'(.+)'::jsonb/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[1].replace(/''/g, "'"));
      }
    }

    if (value.startsWith("'") && value.endsWith("'")) {
      return value.slice(1, -1).replace(/''/g, "'");
    }

    if (!isNaN(Number(value))) {
      return Number(value);
    }

    return value;
  };

  const importDatabase = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !supabase) return;

    setIsImporting(true);
    setImportStatus('SQL dosyası içe aktarılıyor...');

    try {
      const text = await file.text();

      const insertStatements = text.match(/INSERT INTO (\w+) \(([^)]+)\) VALUES \(([^;]+)\);/g);

      if (!insertStatements || insertStatements.length === 0) {
        throw new Error('SQL dosyasında geçerli INSERT ifadeleri bulunamadı');
      }

      const tableData: { [key: string]: any[] } = {};

      for (const statement of insertStatements) {
        const match = statement.match(/INSERT INTO (\w+) \(([^)]+)\) VALUES \(([^;]+)\);/);
        if (!match) continue;

        const [, tableName, columnsStr, valuesStr] = match;
        const columns = columnsStr.split(',').map(c => c.trim());
        const values = valuesStr.split(',').map(v => v.trim());

        const rowData: any = {};
        columns.forEach((col, idx) => {
          const isJsonb = values[idx]?.includes('::jsonb');
          rowData[col] = parseSqlValue(values[idx], isJsonb);
        });

        if (!tableData[tableName]) {
          tableData[tableName] = [];
        }
        tableData[tableName].push(rowData);
      }

      let importedCount = 0;
      const tables = Object.keys(tableData);

      for (const table of tables) {
        const data = tableData[table];

        if (data && data.length > 0) {
          await supabase
            .from(table)
            .delete()
            .neq('id', '00000000-0000-0000-0000-000000000000');

          const { error: insertError } = await supabase
            .from(table)
            .insert(data);

          if (insertError) {
            console.warn(`${table} tablosu içe aktarılamadı:`, insertError);
          } else {
            importedCount++;
          }
        }
      }

      setImportStatus(`${importedCount} tablo başarıyla içe aktarıldı! Sayfa yenileniyor...`);

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      setImportStatus('İçe aktarma hatası: ' + error.message);
      setTimeout(() => setImportStatus(''), 5000);
    } finally {
      setIsImporting(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const saveAdminPin = async () => {
    if (!adminPin || adminPin.length < 6) {
      setPinMessage('PIN kodu en az 6 karakter olmalıdır');
      setPinStatus('error');
      return;
    }

    if (!supabase) {
      setPinMessage('Veritabanı bağlantısı yok');
      setPinStatus('error');
      return;
    }

    setIsSavingPin(true);
    setPinMessage('PIN kaydediliyor...');

    try {
      const { error } = await supabase
        .from('admin_settings')
        .upsert({
          setting_key: 'admin_pin',
          setting_value: { pin: adminPin },
          description: 'Admin panel giriş PIN kodu'
        }, {
          onConflict: 'setting_key'
        })
        .select()
        .maybeSingle();

      if (error) {
        throw new Error('PIN kayıt hatası: ' + error.message);
      }

      setPinMessage('PIN başarıyla kaydedildi! Sayfa yenileniyor...');
      setPinStatus('success');
      setAdminPin('');

      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      setPinMessage('Kayıt hatası: ' + error.message);
      setPinStatus('error');
    } finally {
      setIsSavingPin(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center mb-6">
          <Database className="h-6 w-6 text-red-600 mr-3" />
          <h2 className="text-xl font-bold text-gray-800">Supabase API Ayarları</h2>
        </div>

        {currentConnection && (
          <div className={`mb-6 p-4 rounded-xl border flex items-start ${
            connectionStatus === 'success'
              ? 'bg-green-50 border-green-200'
              : connectionStatus === 'error'
              ? 'bg-red-50 border-red-200'
              : 'bg-gray-50 border-gray-200'
          }`}>
            <div className={`p-2 rounded-full mr-3 mt-0.5 ${
              connectionStatus === 'success'
                ? 'bg-green-100'
                : connectionStatus === 'error'
                ? 'bg-red-100'
                : 'bg-gray-100'
            }`}>
              {connectionStatus === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : connectionStatus === 'error' ? (
                <XCircle className="h-5 w-5 text-red-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-gray-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className={`font-semibold text-sm mb-1 ${
                connectionStatus === 'success'
                  ? 'text-green-800'
                  : connectionStatus === 'error'
                  ? 'text-red-800'
                  : 'text-gray-800'
              }`}>
                Aktif Bağlantı
              </h3>
              <p className={`text-xs mb-1 ${
                connectionStatus === 'success'
                  ? 'text-green-700'
                  : connectionStatus === 'error'
                  ? 'text-red-700'
                  : 'text-gray-700'
              }`}>
                Proje ID: <span className="font-mono font-medium">{currentConnection.project}</span>
              </p>
              <p className={`text-xs truncate ${
                connectionStatus === 'success'
                  ? 'text-green-600'
                  : connectionStatus === 'error'
                  ? 'text-red-600'
                  : 'text-gray-600'
              }`}>
                {currentConnection.url}
              </p>
              {statusMessage && (
                <p className={`text-xs mt-2 font-medium ${
                  connectionStatus === 'success'
                    ? 'text-green-700'
                    : connectionStatus === 'error'
                    ? 'text-red-700'
                    : 'text-gray-700'
                }`}>
                  {statusMessage}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Supabase URL
            </label>
            <input
              type="text"
              value={apiUrl}
              onChange={(e) => {
                setApiUrl(e.target.value);
                setConnectionStatus('idle');
              }}
              placeholder="https://your-project.supabase.co"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm font-mono"
            />
            <p className="mt-1 text-xs text-gray-500">
              Supabase projenizin URL adresi
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Supabase Anon Key
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  setConnectionStatus('idle');
                }}
                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm font-mono"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showApiKey ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Supabase projenizin anon/public API anahtarı
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              onClick={testConnection}
              disabled={isTesting || !apiUrl || !apiKey}
              className="flex-1 flex items-center justify-center px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isTesting ? 'animate-spin' : ''}`} />
              {isTesting ? 'Test Ediliyor...' : 'Bağlantıyı Test Et'}
            </button>

            <button
              onClick={handleSave}
              disabled={isSaving || connectionStatus !== 'success'}
              className="flex-1 flex items-center justify-center px-4 py-3 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-lg hover:from-red-700 hover:to-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm shadow-md"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Kaydediliyor...' : 'Kaydet ve Uygula'}
            </button>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">Önemli Notlar:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>API bilgilerinizi Supabase Dashboard'dan alabilirsiniz</li>
                <li>Değişiklikler için "Bağlantıyı Test Et" butonuna tıklayın</li>
                <li>Test başarılı olduktan sonra "Kaydet ve Uygula" butonuna tıklayın</li>
                <li>API anahtarları hem veritabanına hem localStorage'a kaydedilir</li>
                <li>Veritabanı kaydı sayesinde tüm cihazlardan erişilebilir</li>
                <li>Kayıt sonrası sayfa otomatik olarak yenilenecektir</li>
                <li>API anahtarınızı kimseyle paylaşmayın</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Admin PIN Section */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center mb-6">
          <Shield className="h-6 w-6 text-orange-600 mr-3" />
          <h2 className="text-xl font-bold text-gray-800">Admin PIN Ayarları</h2>
        </div>

        {pinMessage && (
          <div className={`mb-6 p-4 rounded-xl border flex items-start ${
            pinStatus === 'success'
              ? 'bg-green-50 border-green-200'
              : pinStatus === 'error'
              ? 'bg-red-50 border-red-200'
              : 'bg-blue-50 border-blue-200'
          }`}>
            <div className={`p-2 rounded-full mr-3 mt-0.5 ${
              pinStatus === 'success'
                ? 'bg-green-100'
                : pinStatus === 'error'
                ? 'bg-red-100'
                : 'bg-blue-100'
            }`}>
              {pinStatus === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : pinStatus === 'error' ? (
                <XCircle className="h-5 w-5 text-red-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <p className={`text-sm font-medium ${
              pinStatus === 'success'
                ? 'text-green-700'
                : pinStatus === 'error'
                ? 'text-red-700'
                : 'text-blue-700'
            }`}>
              {pinMessage}
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Yeni Admin PIN Kodu
            </label>
            <div className="relative">
              <input
                type={showAdminPin ? 'text' : 'password'}
                value={adminPin}
                onChange={(e) => {
                  setAdminPin(e.target.value);
                  setPinStatus('idle');
                  setPinMessage('');
                }}
                placeholder="En az 6 karakterden oluşan PIN kodu girin"
                className="w-full p-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm font-mono"
              />
              <button
                type="button"
                onClick={() => setShowAdminPin(!showAdminPin)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showAdminPin ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Admin paneline giriş için kullanılacak PIN kodu
            </p>
          </div>

          <button
            onClick={saveAdminPin}
            disabled={isSavingPin || !adminPin || adminPin.length < 6}
            className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-orange-600 to-orange-500 text-white rounded-lg hover:from-orange-700 hover:to-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm shadow-md"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSavingPin ? 'Kaydediliyor...' : 'PIN Kodunu Kaydet'}
          </button>
        </div>

        <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-orange-800">
              <p className="font-medium mb-2">PIN Kodu Uyarıları:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>PIN kodu en az 6 karakterden oluşmalıdır</li>
                <li>PIN kodu veritabanında güvenli şekilde saklanır</li>
                <li>PIN kodunu değiştirdiğinizde sayfa otomatik yenilenecek</li>
                <li>Yeni PIN kodu hemen geçerli olacaktır</li>
                <li>PIN kodunu güvenli bir yerde kaydedin</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Database Backup Section */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center mb-6">
          <FileText className="h-6 w-6 text-red-600 mr-3" />
          <h2 className="text-xl font-bold text-gray-800">Veritabanı Yedekleme</h2>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          Tüm veritabanı içeriğinizi hazır SQL formatında dışa aktarabilir veya daha önce aldığınız SQL yedeğini içe aktarabilirsiniz.
        </p>

        {importStatus && (
          <div className={`mb-6 p-4 rounded-xl border flex items-center ${
            importStatus.includes('başarı')
              ? 'bg-green-50 border-green-200 text-green-700'
              : importStatus.includes('hata')
              ? 'bg-red-50 border-red-200 text-red-700'
              : 'bg-blue-50 border-blue-200 text-blue-700'
          }`}>
            <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
            <span className="text-sm font-medium">{importStatus}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={exportDatabase}
            disabled={isExporting || !supabase}
            className="flex items-center justify-center px-6 py-4 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-xl hover:from-green-700 hover:to-green-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-md hover:shadow-lg"
          >
            <Download className={`h-5 w-5 mr-2 ${isExporting ? 'animate-bounce' : ''}`} />
            {isExporting ? 'Dışa Aktarılıyor...' : 'Veritabanını Dışa Aktar'}
          </button>

          <label className={`flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:from-blue-700 hover:to-blue-600 transition-all font-medium shadow-md hover:shadow-lg cursor-pointer ${
            isImporting || !supabase ? 'opacity-50 cursor-not-allowed' : ''
          }`}>
            <Upload className={`h-5 w-5 mr-2 ${isImporting ? 'animate-bounce' : ''}`} />
            {isImporting ? 'İçe Aktarılıyor...' : 'SQL Dosyasını Yükle'}
            <input
              type="file"
              accept=".sql,.txt"
              onChange={importDatabase}
              disabled={isImporting || !supabase}
              className="hidden"
            />
          </label>
        </div>

        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium mb-2">Yedekleme Uyarıları:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li><strong>Dışa Aktar:</strong> Tüm tablolarınızı (hızlı yanıtlar, yanıt kalıpları, karşılama mesajları, ayarlar) hazır SQL INSERT komutları olarak kaydeder</li>
                <li><strong>İçe Aktar:</strong> SQL dosyasını okur, mevcut tüm verileri siler ve yedekteki verilerle değiştirir</li>
                <li><strong>SQL Formatı:</strong> Dışa aktarılan .sql dosyası direkt olarak Supabase SQL Editor'de çalıştırılabilir</li>
                <li><strong>Önemli:</strong> İçe aktarma işlemi geri alınamaz! Önce mevcut verilerinizi yedekleyin</li>
                <li>Yedek dosyalarınızı güvenli bir yerde saklayın ve düzenli olarak yedek almayı unutmayın</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiSettings;
